import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NewCriticalDateComponent } from '../../create-new/new-critical-date/new-critical-date.component';
import { EntityType } from '../../objects/interface-library';
import { CriticalDate } from '../../objects/critical-dates';
import { Observable, catchError, throwError, switchMap, of, forkJoin, map } from 'rxjs';
import { BaaService } from '../entity-services/baa.service';
import { CoiService } from '../entity-services/coi.service';
import { CriticalDatesService } from '../entity-services/critical-dates.service';



@Injectable({
  providedIn: 'root'
})
export class DateDialogService {
  private baaService = inject(BaaService);
  private coiService = inject(CoiService);
  private criticalDatesService = inject(CriticalDatesService);
  private dialog = inject(MatDialog);

    public openModalDateDialog(entityId: string, entityType: EntityType, entity: any): void {
      const dialogData = {
        entityId: entityId,
        entityType: entityType,
        message: 'Add Important Date',
        title: 'Add Important Date',
      };

      const dialogRef = this.dialog.open(NewCriticalDateComponent, {
        width: '1200px',
        height: '1200px',
        data: dialogData
      });

      dialogRef.afterClosed().subscribe(dialogResult => {
        if (dialogResult) {
          // Delegate all the data-related logic to the service
          this.createCriticalDateAndAssociate(
            dialogResult,
            entityId,
            entityType
          ).subscribe({
            next: ({ criticalDate, updatedEntity }) => {
              // Update the component's state with the results from the service
              entity = updatedEntity;
              console.log('Critical date added and entity updated successfully.');
            },
            error: (err) => {
              console.error('An error occurred during critical date creation:', err);
            }
          });
        }
      });
    }


  /**
   * Creates a new CriticalDate and associates it with a specific entity (e.g., a BAA).
   * This method:
   * 1. Writes the critical date to the critical-dates database table
   * 2. Gets back the created date with its criticalDateNum
   * 3. Adds ONLY the criticalDateNum to the entity's criticalDates array (as a reference)
   * 4. Writes the updated entity back to the database
   *
   * @param newDate The CriticalDate object returned from the dialog.
   * @param entity The entity (e.g., a BAA) to associate with.
   * @param entityType The type of the entity (e.g., EntityType.Baa).
   * @returns An observable of the fully-hydrated CriticalDate and the updated entity.
   */
  public createCriticalDateAndAssociate(
    newDate: any,
    entity: any,
    entityType: EntityType
  ): Observable<{ criticalDate: CriticalDate, updatedEntity: any }> {

    // Step 1: Write the critical date to its own database table
    console.log('[DateDialogService] Creating critical date:', newDate);
    const writeDate$ = this.criticalDatesService.createCriticalDate(newDate);

    return writeDate$.pipe(
      switchMap(writtenDate => {
        if (!writtenDate || !writtenDate.criticalDateNum) {
          console.error('[DateDialogService] Failed to create critical date - no criticalDateNum returned');
          return throwError(() => new Error('Failed to create critical date'));
        }

        console.log('[DateDialogService] Critical date created with criticalDateNum:', writtenDate.criticalDateNum);

        // Step 2: Add ONLY the criticalDateNum (not the whole object) to the entity's array
        // This creates a proper relational reference (foreign key pattern)
        const existingDateNums = entity.criticalDates || [];
        const updatedCriticalDates = [...existingDateNums, writtenDate.criticalDateNum];

        console.log('[DateDialogService] Updating entity with criticalDates:', updatedCriticalDates);

        // Step 3: Use a switch statement to determine the correct service and write function
        let writeEntity$: Observable<any>;
        switch (entityType) {
          case EntityType.Baa:
            writeEntity$ = this.baaService.writeBaa({ ...entity, criticalDates: updatedCriticalDates });
            break;
          // BUGBUG ANGULAR 20 UPGRADE
          // case EntityType.Contract:
          //   writeEntity$ = this.contractService.writeContract({ ...entity, criticalDates: updatedCriticalDates });
          //   break;
          case EntityType.Coi:
            writeEntity$ = this.coiService.writeCoi({ ...entity, criticalDates: updatedCriticalDates });
            break;
          default:
            console.error(`No write service found for entity type: ${entityType}`);
            return throwError(() => new Error(`No write service found for entity type: ${entityType}`));
        }

        // Step 4: Return both the created date and the updated entity
        return forkJoin({
          updatedEntity: writeEntity$,
          criticalDate: of(writtenDate) // Return the full critical date object from the API
        });
      }),
      catchError(error => {
        console.error('[DateDialogService] Error creating or associating critical date:', error);
        return throwError(() => error);
      })
    );
  }






}
